
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface TeamMessage {
  id: string;
  team_id: string;
  sender_id: string;
  message: string;
  created_at: string;
  updated_at: string;
  profiles?: {
    username: string;
    farm_name: string;
    avatar_url: string;
  };
}

export const useTeamMessages = (teamId?: string) => {
  const [messages, setMessages] = useState<TeamMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchMessages = async () => {
    if (!teamId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log('🔍 Fetching messages for team:', teamId);
      
      // First, get the messages
      const { data: messagesData, error: messagesError } = await supabase
        .from('team_messages')
        .select('*')
        .eq('team_id', teamId)
        .order('created_at', { ascending: true });

      if (messagesError) {
        console.error('❌ Error fetching team messages:', messagesError);
        setError(messagesError.message);
        return;
      }

      // Then, get the profile data for each unique sender
      const senderIds = [...new Set(messagesData?.map(msg => msg.sender_id) || [])];
      const profilesMap = new Map();

      if (senderIds.length > 0) {
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('user_id, username, farm_name, avatar_url')
          .in('user_id', senderIds);

        profilesData?.forEach(profile => {
          profilesMap.set(profile.user_id, {
            username: profile.username || 'Unknown User',
            farm_name: profile.farm_name || 'Unknown Farm',
            avatar_url: profile.avatar_url || null
          });
        });
      }

      // Combine messages with profile data
      const messagesWithProfiles = messagesData?.map(message => ({
        ...message,
        profiles: profilesMap.get(message.sender_id) || {
          username: 'Unknown User',
          farm_name: 'Unknown Farm',
          avatar_url: null
        }
      })) || [];

      console.log('✅ Successfully fetched team messages:', messagesWithProfiles);
      setMessages(messagesWithProfiles);
      
    } catch (err) {
      console.error('❌ Error in fetchMessages:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (teamId: string, message: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to send messages",
          variant: "destructive"
        });
        return null;
      }

      console.log('🚀 Sending message to team:', teamId);

      const { data, error } = await supabase
        .from('team_messages')
        .insert({
          team_id: teamId,
          sender_id: user.id,
          message: message.trim()
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Error sending message:', error);
        toast({
          title: "Failed to send message",
          description: error.message,
          variant: "destructive"
        });
        return null;
      }

      console.log('✅ Message sent successfully:', data);
      
      toast({
        title: "Message sent",
        description: "Your message has been sent to the team"
      });

      return data;

    } catch (error: any) {
      console.error('❌ Error sending message:', error);
      toast({
        title: "Failed to send message",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive"
      });
      return null;
    }
  };

  // Set up real-time subscription
  useEffect(() => {
    let channel: any = null;

    if (teamId) {
      console.log('🔗 Setting up real-time subscription for team:', teamId);
      
      channel = supabase
        .channel(`team-messages-${teamId}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'team_messages',
            filter: `team_id=eq.${teamId}`
          },
          async (payload) => {
            console.log('📨 New message received:', payload);
            
            // Get profile data for the sender
            const { data: profileData } = await supabase
              .from('profiles')
              .select('username, farm_name, avatar_url')
              .eq('user_id', payload.new.sender_id)
              .single();

            const newMessage: TeamMessage = {
              id: payload.new.id,
              team_id: payload.new.team_id,
              sender_id: payload.new.sender_id,
              message: payload.new.message,
              created_at: payload.new.created_at,
              updated_at: payload.new.updated_at,
              profiles: profileData || {
                username: 'Unknown User',
                farm_name: 'Unknown Farm',
                avatar_url: null
              }
            };

            setMessages(prev => [...prev, newMessage]);
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'team_messages',
            filter: `team_id=eq.${teamId}`
          },
          async (payload) => {
            console.log('✏️ Message updated:', payload);
            
            // Get profile data for the sender
            const { data: profileData } = await supabase
              .from('profiles')
              .select('username, farm_name, avatar_url')
              .eq('user_id', payload.new.sender_id)
              .single();

            const updatedMessage: TeamMessage = {
              id: payload.new.id,
              team_id: payload.new.team_id,
              sender_id: payload.new.sender_id,
              message: payload.new.message,
              created_at: payload.new.created_at,
              updated_at: payload.new.updated_at,
              profiles: profileData || {
                username: 'Unknown User',
                farm_name: 'Unknown Farm',
                avatar_url: null
              }
            };

            setMessages(prev => 
              prev.map(msg => 
                msg.id === updatedMessage.id ? updatedMessage : msg
              )
            );
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'DELETE',
            schema: 'public',
            table: 'team_messages',
            filter: `team_id=eq.${teamId}`
          },
          (payload) => {
            console.log('🗑️ Message deleted:', payload);
            setMessages(prev => 
              prev.filter(msg => msg.id !== payload.old.id)
            );
          }
        )
        .subscribe((status) => {
          console.log('📡 Real-time subscription status:', status);
        });
    }

    return () => {
      if (channel) {
        console.log('🔌 Cleaning up real-time subscription');
        supabase.removeChannel(channel);
      }
    };
  }, [teamId]);

  // Initial fetch
  useEffect(() => {
    fetchMessages();
  }, [teamId]);

  return {
    messages,
    loading,
    error,
    fetchMessages,
    sendMessage
  };
};
